/**
 * ============================================================================
 * useSnapshotsQuery — Phase 6b frontend (Snapshot Signing Tray)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for the monthly quality-metric snapshot list.
 *          Snapshots are produced by metrics-svc at month boundaries; each
 *          snapshot is content-hashed and must be signed by the QA Director
 *          (TENANT scope) or Site Quality Head (SITE scope) before it can be
 *          released into the audit trail.
 * Endpoint: GET /api/v1/quality-metrics/snapshots
 *
 * NOTE on payload: each snapshot carries a `metrics_summary` field — a
 *  Map<string, unknown> — that we deliberately leave loosely typed. The
 *  signing dialog renders a deterministic list view of the keys it knows
 *  about and falls back to a generic "key: value" table for the rest.
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type SnapshotScope = 'TENANT' | 'SITE';
export type SnapshotStatus = 'PENDING_SIGN' | 'SIGNED' | 'VOIDED';

export interface QualitySnapshot {
  snapshot_id: string;
  scope: SnapshotScope;
  /** Site code when scope=SITE; null when scope=TENANT. */
  site_code: string | null;
  /** ISO-8601 date (YYYY-MM-DD) — inclusive start of the period. */
  period_start: string;
  /** ISO-8601 date (YYYY-MM-DD) — inclusive end of the period. */
  period_end: string;
  status: SnapshotStatus;
  /** Lower-case hex SHA-256 of the canonical metric payload. */
  content_hash: string;
  /** Display name of the signer (null until signed). */
  signed_by: string | null;
  signed_at_utc: string | null;
  signature_bundle_id: string | null;
  generated_at_utc: string;
  /** Aggregated metric summary surface used by the signing dialog. */
  metrics_summary: Record<string, unknown>;
}

export interface SnapshotsResponse {
  items: QualitySnapshot[];
}

/**
 * Mock-api / contract-drift normalizer. The mock-api returns
 * `period_start_utc` + `period_end_utc` + sometimes `computed_at_utc` while
 * the screen expects `period_start` + `period_end` + `generated_at_utc`.
 * Coerce here so the date-formatter never sees `undefined`.
 */
function normalizeSnapshots(raw: unknown): SnapshotsResponse {
  const items = Array.isArray((raw as any)?.items) ? (raw as any).items : [];
  const todayIso = new Date().toISOString();
  const mapped: QualitySnapshot[] = items.map((it: any) => ({
    snapshot_id: it.snapshot_id ?? it.id ?? '?',
    scope_type: it.scope_type ?? 'TENANT',
    scope_id: it.scope_id ?? null,
    period_start: it.period_start ?? it.period_start_utc ?? todayIso,
    period_end:   it.period_end   ?? it.period_end_utc   ?? todayIso,
    status:       it.status       ?? 'DRAFT',
    signed_by_user_id: it.signed_by_user_id ?? null,
    signed_at_utc:     it.signed_at_utc     ?? null,
    signature_bundle_id: it.signature_bundle_id ?? null,
    generated_at_utc:    it.generated_at_utc    ?? it.computed_at_utc ?? it.period_end_utc ?? todayIso,
    metrics_summary:     it.metrics_summary     ?? {},
  }));
  return { items: mapped };
}

export function useSnapshotsQuery(): UseQueryResult<SnapshotsResponse> {
  return useQuery<SnapshotsResponse>({
    queryKey: ['quality-metrics', 'snapshots'],
    queryFn: ({ signal }) =>
      apiFetch<unknown>('/api/v1/quality-metrics/snapshots', { signal }).then(normalizeSnapshots),
  });
}
